import dayjs from 'dayjs';
import { StandingOrderFrequency } from 'types/transactions.types';
import { getTransferType } from '../../helpers';
import { TransferReasons, type TransferReviewData } from '../types';

/**
 * Creates standing order properties from schedule data
 * Returns empty object for regular payments (OneTime with today's date)
 */
const createStandingOrderProps = (
  schedule: TransferReviewData['schedule'],
): Record<string, any> => {
  const firstExecutionDateIsToday = dayjs(schedule.firstExecutionDate).isSame(
    dayjs(),
    'day',
  );

  const isRegularPayment =
    schedule.period === StandingOrderFrequency.OneTime &&
    firstExecutionDateIsToday;

  if (isRegularPayment) {
    return {};
  }

  if (schedule.period === StandingOrderFrequency.OneTime) {
    return {
      requestedExecutionAt: dayjs(schedule.firstExecutionDate).toISOString(),
    };
  }

  return {
    period: schedule.period,
    firstExecutionDate: dayjs(schedule.firstExecutionDate).toISOString(),
    lastExecutionDate: schedule.lastExecutionDate
      ? dayjs(schedule.lastExecutionDate).toISOString()
      : undefined,
  };
};

/**
 * Creates the onConfirm function for transfer execution
 * Extracted from MoneyTransfer component to improve testability and reusability
 */
export const createTransferConfirmHandler = (
  reviewData: TransferReviewData,
  sourceAccountId?: string,
  destinationAccountId?: string,
) => {
  if (!reviewData.execution?.mutateFunction) {
    console.error('Missing mutateFunction in reviewData.execution');
    return undefined;
  }

  return () => {
    const mutateFunction = reviewData.execution!.mutateFunction!;
    const originalFormData = reviewData.execution!.originalFormData!;

    // Check if this is an internal transfer (no recipient IBAN/name initially)
    const isInternalTransfer =
      !reviewData.recipientIBAN && !reviewData.recipientName;

    // Check if it's a regular payment (OneTime period with today's date)
    const firstExecutionDateIsToday = dayjs(
      reviewData.schedule.firstExecutionDate,
    ).isSame(dayjs(), 'day');

    const isRegularPayment =
      reviewData.schedule.period === StandingOrderFrequency.OneTime &&
      firstExecutionDateIsToday;

    if (isInternalTransfer && isRegularPayment) {
      // Regular internal transfer (immediate) - use toConnectorId
      const toConnectorId =
        destinationAccountId ||
        reviewData.execution?.originalFormData?.toConnectorId;

      if (!toConnectorId) {
        console.error('Missing destination account ID for internal transfer');
        return;
      }

      const internalTransferBody = {
        fromConnectorId: sourceAccountId,
        toConnectorId,
        amount: { value: reviewData.amount, currency: 'EUR' },
        reason: 'transfer',
        type: 'internal',
      };
      mutateFunction(internalTransferBody);
    } else {
      // Scheduled/standing order transfers - use beneficiary structure
      // This includes:
      // 1. External scheduled transfers (OneTime future)
      // 2. External standing orders (recurring)
      // 3. Internal standing orders (recurring) - uses internal account IBAN
      const standingOrderProps = createStandingOrderProps(reviewData.schedule);

      // For internal standing orders, use the destination account IBAN from originalFormData
      const beneficiaryIBAN = isInternalTransfer
        ? originalFormData.destinationAccountIBAN
        : reviewData.recipientIBAN;

      const beneficiaryName = isInternalTransfer
        ? originalFormData.destinationAccountName
        : reviewData.recipientName;

      if (!beneficiaryIBAN || !beneficiaryName) {
        console.error('Missing beneficiary IBAN or name for transfer');
        return;
      }

      const transferBody = {
        fromConnectorId: sourceAccountId,
        communication: {
          message: reviewData.communication || '',
          type: originalFormData.communication_type || 'unstructured',
        },
        amount: { value: reviewData.amount, currency: 'EUR' },
        reason: originalFormData.reason || TransferReasons.transfer,
        beneficiary: {
          iban: beneficiaryIBAN,
          name: beneficiaryName,
          // Include verification token for external transfers if available
          verificationToken: originalFormData.verificationToken,
        },
        type: getTransferType(
          originalFormData.instant,
          reviewData.schedule.period as StandingOrderFrequency,
        ),
        ...(originalFormData.document && {
          document: originalFormData.document,
        }),
        ...standingOrderProps,
      };
      mutateFunction(transferBody);
    }
  };
};

/**
 * Updates schedule data in review data and recreates the onConfirm handler
 */
export const updateTransferSchedule = (
  reviewData: TransferReviewData,
  scheduleData: {
    period: StandingOrderFrequency;
    firstExecutionDate: Date;
    lastExecutionDate?: Date;
  },
  sourceAccountId?: string,
  destinationAccountId?: string,
): TransferReviewData => {
  const updatedReviewData = {
    ...reviewData,
    schedule: {
      period: scheduleData.period,
      firstExecutionDate: scheduleData.firstExecutionDate,
      lastExecutionDate: scheduleData.lastExecutionDate,
      requestedExecutionAt:
        scheduleData.period === StandingOrderFrequency.OneTime
          ? scheduleData.firstExecutionDate
          : undefined,
    },
  };

  // Recreate onConfirm function with updated data
  const onConfirm = createTransferConfirmHandler(
    updatedReviewData,
    sourceAccountId,
    destinationAccountId,
  );

  return {
    ...updatedReviewData,
    execution: {
      ...updatedReviewData.execution,
      onConfirm,
    },
  };
};
